import dayjs from "dayjs";

/* ----------------------------
   GENERIC SAFETY HELPERS
---------------------------- */

export const safe = (v, fb = "-") =>
  v === null || v === undefined || v === "" ? fb : v;

export const asList = (v) => {
  if (!v) return [];
  if (Array.isArray(v)) return v.filter(Boolean);
  if (typeof v === "string") return [v].filter(Boolean);
  return [String(v)].filter(Boolean);
};

/* ----------------------------
   NAME / INITIALS
---------------------------- */

export const displayName = (a = {}) => {
  const first = (a?.preferredName || a?.firstName || "").trim();
  const last = (a?.lastName || "").trim();
  return (
    [first, last].filter(Boolean).join(" ").trim() || a?.email || "Adviser"
  );
};

export const initialsFrom = (a = {}) => {
  const f = (a?.preferredName || a?.firstName || "").trim();
  const l = (a?.lastName || "").trim();
  const first = f ? f[0] : "";
  const last = l ? l[0] : "";
  return (first + last).toUpperCase() || "A";
};

/* ----------------------------
   COLOUR NORMALISER
---------------------------- */

export const cssColor = (c, fallback = "var(--Brand1-PenBlue)") => {
  if (!c) return fallback;
  const s = String(c).trim();
  if (!s) return fallback;
  return s.startsWith("#") ? s : `#${s}`;
};

/* ----------------------------
   DATE FORMATTERS (dayjs)
---------------------------- */

export const fmtDate = (v, fb = "-") => {
  if (!v) return fb;
  const d = dayjs(v?.seconds ? v.seconds * 1000 : v);
  return d.isValid() ? d.format("DD MMM YYYY") : fb;
};

export const fmtMonthYear = (v, fb = "-") => {
  if (!v) return fb;
  const d = dayjs(v?.seconds ? v.seconds * 1000 : v);
  return d.isValid() ? d.format("MMM YYYY") : fb;
};

export const fmtHuman = (v, fb = "-") => {
  if (!v) return fb;
  if (typeof v === "string" || typeof v === "number") return v;
  if (v?.seconds) return fmtDate(v);
  return fb;
};

/* ----------------------------
   ADDRESS FORMATTER
---------------------------- */

export const formatAddress = (p) => {
  if (!p) return "-";
  if (typeof p === "string") return p;

  const parts = [
    p.fullAddress,
    p.address,
    p.line1,
    p.line2,
    p.town,
    p.county,
    p.postcode,
  ].filter(Boolean);

  return parts.join(", ") || "-";
};

/* ----------------------------
   UK PHONE FORMATTER (ROBUST)
---------------------------- */

export const formatUKPhone = (input) => {
  if (!input) return "-";

  // Handle arrays
  if (Array.isArray(input)) {
    return formatUKPhone(input[0]);
  }

  let raw = String(input).trim();
  if (!raw) return "-";

  // Remove all non-digits
  let digits = raw.replace(/\D/g, "");

  // Normalize country code
  if (digits.startsWith("0044")) {
    digits = "0" + digits.slice(4);
  } else if (digits.startsWith("44")) {
    digits = "0" + digits.slice(2);
  }

  if (!digits.startsWith("0")) return digits;

  // Mobile
  if (digits.startsWith("07") && digits.length === 11) {
    return `${digits.slice(0, 5)} ${digits.slice(5)}`;
  }

  // Landline
  if (digits.length === 11) {
    return `${digits.slice(0, 4)} ${digits.slice(4, 7)} ${digits.slice(7)}`;
  }

  return digits;
};


export const norm = (v) => (v ?? "").toString().trim().toLowerCase();

 // Handles Firestore Timestamp ({seconds,nanoseconds}), Date, ISO string, etc.
export const fmtMaybeDate = (v) => {
  if (!v) return "-";
  if (typeof v === "object" && v?.seconds != null) return fmtHuman(v); // your fmtHuman should handle it
  if (v instanceof Date) return fmtHuman(v);
  // try to format any date-ish string/number
  return fmtHuman(v) || safe(v);
};

export const fmtMaybeMonthYear = (v) => {
  if (!v) return "-";
  if (typeof v === "object" && v?.seconds != null) return fmtMonthYear(v);
  return fmtMonthYear(v) || safe(v);
};

// ---------- Status → colour ----------
export function statusColor(statusArr) {
  const hasFP = Array.isArray(statusArr) && statusArr.includes("FP");
  const hasMA = Array.isArray(statusArr) && statusArr.includes("MA");
  const hasNAP = Array.isArray(statusArr) && statusArr.includes("NAP");
  if (hasFP && hasMA) return "#3f1c7bff"; // purple
  if (hasFP) return "#9e145bff"; // blue
  if (hasMA) return "#ed7600ff"; // green
  if (hasNAP) return "#096039ff"; // yellow
  return "#FFEE74"; // grey
}

export const YESNO = (v) => (v === true ? "Yes" : v === false ? "No" : "—");

export const pickTruthy = (obj, keys) => keys.filter((k) => obj?.[k] === true);

export const pickAll = (obj, keys) => keys.map((k) => ({ key: k, value: obj?.[k] }));

export const QUAL_GROUPS = [
  {
    title: "CII",
    keys: [
      "FPC1",
      "FPC2",
      "FPC3",
      "CF1",
      "CF2",
      "CF3",
      "CF4",
      "CF5",
      "CF6",
      "MAQ",
    ],
  },
  {
    title: "Walbrook Institute London (LIBF/IFS)",
    keys: [
      "CeMAP1",
      "CeMAP2",
      "CeMAP3",
      "CeMAP_Bridge",
      "CeFA1",
      "CeFA2",
      "CeFA3",
      "CeFA4",
      "IFS_DIPLOMA",
    ],
  },
  {
    title: "CII JO",
    keys: [
      "JO1",
      "JO2",
      "JO3",
      "JO4",
      "JO5",
      "JO6",
      "JO7",
      "JO8",
      "JO9",
      "JO10",
      "JO11",
      "JO12",
    ],
  },
  {
    title: "CII RO",
    keys: ["RO1", "RO2", "RO3", "RO4", "RO5", "RO6", "RO7", "RO8"],
  },
  { title: "Other", keys: ["CII_DIPLOMA", "Equity_Release", "Long_Term_Care"] },
];
